/**
 * WebHID foot-pedal adapter.
 * Maps the first report byte to play / rewind / fastForward / pause / none.
 * Value mapped to pause (typically 0) is treated as release/idle.
 */

/** @typedef {(event: { action: string, phase: 'down' | 'up' | 'change', raw?: number }) => void} PedalEventHandler */

/**
 * @param {{
 *   device: HIDDevice,
 *   getMapping: () => Record<string | number, string>,
 *   onPedalEvent: PedalEventHandler
 * }} options
 */
export function createWebHidAdapter({ device, getMapping, onPedalEvent }) {
    /** @type {((event: HIDInputReportEvent) => void) | null} */
    let onReport = null;
    /** @type {string} */
    let lastAction = "pause";

    function handleReport(event) {
        const value = event.data.getUint8(0);
        const mapping = getMapping() || {};
        const action = mapping[value] ?? mapping[String(value)] ?? "none";

        if (action === "pause" || action === "none") {
            if (lastAction !== "pause" && lastAction !== "none") {
                onPedalEvent({
                    action: lastAction,
                    phase: "up",
                    raw: value,
                });
            }
            lastAction = "pause";
            return;
        }

        if (action === lastAction) {
            return;
        }

        if (lastAction !== "pause" && lastAction !== "none") {
            onPedalEvent({ action: lastAction, phase: "up", raw: value });
        }

        lastAction = action;
        onPedalEvent({ action, phase: "down", raw: value });
    }

    return {
        async start() {
            if (!device) {
                throw new Error("No HID device selected");
            }
            if (!device.opened) {
                await device.open();
            }
            onReport = handleReport;
            device.addEventListener("inputreport", onReport);
        },

        async stop() {
            if (device && onReport) {
                device.removeEventListener("inputreport", onReport);
            }
            onReport = null;
            lastAction = "pause";
            try {
                if (device?.opened) {
                    await device.close();
                }
            } catch (err) {
                console.warn("[webHidAdapter] close failed", err);
            }
        },
    };
}

/**
 * @returns {Promise<HIDDevice[]>}
 */
export async function listHidDevices() {
    if (!navigator.hid) {
        return [];
    }
    const devices = await navigator.hid.getDevices();
    const seen = new Set();
    return devices.filter((device) => {
        if (!device.productName) return false;
        if (seen.has(device.productId)) return false;
        seen.add(device.productId);
        return true;
    });
}

/**
 * Prompt user to grant a HID device (requires user gesture).
 * @returns {Promise<HIDDevice[]>}
 */
export async function requestHidDevice() {
    if (!navigator.hid) {
        throw new Error("WebHID is not supported in this browser");
    }
    return navigator.hid.requestDevice({ filters: [] });
}
